import React, { createContext, useContext, type ReactNode, useMemo } from "react";
import { GraphNode, type ParsedRuleGraph } from "../../../utils";

export interface GraphContextValue {
  graph: ParsedRuleGraph | null;
}

const GraphContext = createContext<GraphContextValue | null>(null);

export const useGraph = (): GraphContextValue | null => {
  const context = useContext(GraphContext);
  return context;
};

export interface GraphProviderProps {
  children: ReactNode;
  graph: ParsedRuleGraph;
}

const GraphProvider = (props: GraphProviderProps) => {
  const { children, graph } = props;

  const resolvedContext = useMemo(() => {
    return { graph } satisfies GraphContextValue;
  }, [graph]);

  return <GraphContext.Provider value={resolvedContext}>{children}</GraphContext.Provider>;
};

export default GraphProvider;

export interface GraphDependantProps {
  graph?: ParsedRuleGraph;
}

export const useGraphFromPropsOrContext = (
  props: GraphDependantProps,
): {
  graph: ParsedRuleGraph | undefined;
  withGraphProvider: (children: ReactNode) => ReactNode;
} => {
  const { graph } = props;
  const context = useGraph();
  const resolvedGraph = graph || context?.graph;
  return {
    graph: resolvedGraph || undefined,
    withGraphProvider: (children: ReactNode) => {
      if (graph) {
        return <GraphProvider graph={graph}>{children}</GraphProvider>;
      }
      return children;
    },
  };
};
